import { useState, useMemo } from "react";
import { ChevronDown, ChevronRight, Search, Lock, Globe, Users, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

interface Pond {
  id: string;
  name: string;
  type: string;
  isPublic: boolean;
  isOwner: boolean;
  parentId: string | null;
}

interface PondNode extends Pond {
  children: PondNode[];
  depth: number;
}

interface ParentPondPickerProps {
  ponds: Pond[];
  value: string | null;
  onChange: (pondId: string | null) => void;
  placeholder?: string;
}

export const ParentPondPicker = ({
  ponds,
  value,
  onChange,
  placeholder = "Nenhuma (lagoa raiz)",
}: ParentPondPickerProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedPonds, setExpandedPonds] = useState<Set<string>>(new Set());

  // Get selected pond name
  const selectedPond = ponds.find((p) => p.id === value);

  // Build tree structure (only owner ponds can be parents)
  const pondTree = useMemo(() => {
    const ownerPonds = ponds.filter((p) => p.isOwner);
    
    const buildTree = (parentId: string | null, depth: number): PondNode[] => {
      return ownerPonds
        .filter((p) => p.parentId === parentId)
        .map((pond) => ({
          ...pond,
          depth,
          children: buildTree(pond.id, depth + 1),
        }));
    };
    return buildTree(null, 0);
  }, [ponds]);

  // Filter ponds by search
  const filteredPonds = useMemo(() => {
    if (!searchTerm) return pondTree;

    const filterNodes = (nodes: PondNode[]): PondNode[] => {
      return nodes
        .map((node) => ({
          ...node,
          children: filterNodes(node.children),
        }))
        .filter(
          (node) =>
            node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            node.children.length > 0
        );
    };
    return filterNodes(pondTree);
  }, [pondTree, searchTerm]);

  // Flatten tree for searching
  const flatPonds = useMemo(() => {
    const flatten = (nodes: PondNode[]): PondNode[] => {
      return nodes.flatMap((node) => [node, ...flatten(node.children)]);
    };
    return flatten(pondTree);
  }, [pondTree]);

  const toggleExpand = (pondId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedPonds((prev) => {
      const next = new Set(prev);
      if (next.has(pondId)) {
        next.delete(pondId);
      } else {
        next.add(pondId);
      }
      return next;
    });
  };

  const handleSelect = (pondId: string | null) => {
    onChange(pondId);
    setOpen(false);
    setSearchTerm("");
  };

  // Auto-expand parent nodes when searching
  useMemo(() => {
    if (searchTerm) {
      const matchingIds = new Set<string>();
      
      const findMatches = (nodes: PondNode[]) => {
        for (const node of nodes) {
          if (node.name.toLowerCase().includes(searchTerm.toLowerCase())) {
            // Add all parents to expanded set
            let current: PondNode | undefined = node;
            while (current) {
              matchingIds.add(current.id);
              current = flatPonds.find((p) => p.id === current?.parentId);
            }
          }
          findMatches(node.children);
        }
      };
      
      findMatches(pondTree);
      setExpandedPonds(matchingIds);
    }
  }, [searchTerm, pondTree, flatPonds]);

  const renderPondNode = (node: PondNode): React.ReactNode => {
    const isExpanded = expandedPonds.has(node.id);
    const hasChildren = node.children.length > 0;
    const isSelected = node.id === value;

    return (
      <div key={node.id}>
        <button
          type="button"
          className={cn(
            "w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors",
            "hover:bg-accent/50",
            isSelected && "bg-primary/10 text-primary"
          )}
          style={{ paddingLeft: `${node.depth * 16 + 8}px` }}
          onClick={() => handleSelect(node.id)}
        >
          {/* Expand button */}
          {hasChildren ? (
            <button
              type="button"
              onClick={(e) => toggleExpand(node.id, e)}
              className="p-0.5 hover:bg-background/50 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </button>
          ) : (
            <div className="w-4" />
          )}

          {/* Pond icon */}
          <div className={cn(
            "w-7 h-7 rounded-full flex-shrink-0",
            "bg-gradient-to-br from-pond via-pond-light to-pond-surface",
            "flex items-center justify-center text-sm"
          )}>
            🐸
          </div>

          {/* Pond name and info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-sm truncate">{node.name}</span>
              {node.type === "private" ? (
                <Lock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              ) : node.isPublic ? (
                <Globe className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              ) : (
                <Users className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              )}
            </div>
            {hasChildren && (
              <p className="text-[10px] text-muted-foreground">
                {node.children.length} sub-lagoa(s)
              </p>
            )}
          </div>

          {/* Selected indicator */}
          {isSelected && (
            <Check className="h-4 w-4 text-primary flex-shrink-0" />
          )}
        </button>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div>{node.children.map(renderPondNode)}</div>
        )}
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal h-10"
        >
          {selectedPond ? (
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-pond to-pond-light flex items-center justify-center text-xs flex-shrink-0">
                🐸
              </div>
              <span className="truncate">{selectedPond.name}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        {/* Search */}
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar lagoa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
        </div>

        {/* Options */}
        <ScrollArea className="max-h-[280px]">
          <div className="p-2">
            {/* None option */}
            <button
              type="button"
              className={cn(
                "w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors",
                "hover:bg-accent/50",
                value === null && "bg-primary/10 text-primary"
              )}
              onClick={() => handleSelect(null)}
            >
              <div className="w-4" />
              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-sm flex-shrink-0">
                🌿
              </div>
              <span className="font-medium text-sm">{placeholder}</span>
              {value === null && (
                <Check className="h-4 w-4 text-primary flex-shrink-0 ml-auto" />
              )}
            </button>

            {/* Pond tree */}
            {filteredPonds.length > 0 ? (
              <div className="mt-1">
                {filteredPonds.map(renderPondNode)}
              </div>
            ) : searchTerm ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Nenhuma lagoa encontrada
              </div>
            ) : null}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
